import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useLocation } from 'react-router-dom';

import StatusBadge from '../../shared/StatusBadge/StatusBadge';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faClipboardList, faSyncAlt,
  faEye, faPencilAlt, faTrash, faPlus, faSpinner, faTimes,
  faExclamationTriangle, faSearch, faCheckCircle,
  faExclamationCircle, faClock
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import { getAuthHeader } from '../../../utils/auth';
import { getApiEndpoint } from '../../../config/api';
import { sortNewestFirst, insertNewestFirst } from '../../../utils/sortUtils';
import './Inspections.css';

/* ── Helpers ─────────────────────────────────────────────── */
const useIsMobile = (breakpoint = 768) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= breakpoint);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= breakpoint);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [breakpoint]);
  return isMobile;
};

const normStatus = (s) => {
  if (!s) return 'unknown';
  const lower = s.toLowerCase();
  if (lower === 'overdue') return 'overdue';
  if (lower === 'pending') return 'pending';
  if (lower === 'in progress' || lower === 'in_progress') return 'in-progress';
  if (lower === 'completed') return 'completed';
  if (lower === 'failed') return 'failed';
  if (lower === 'cancelled') return 'inactive';
  if (lower === 'scheduled') return 'scheduled';
  if (lower === 'missed') return 'overdue';
  return lower;
};

const fmtDate = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return '—';
  return dt.toLocaleDateString();
};

const fmtDateInput = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return '';
  return dt.toISOString().split('T')[0];
};

/* ── Component ───────────────────────────────────────────── */
export default function Inspections() {
  const { id: propertyId } = useParams();
  const location = useLocation();
  const isMobile = useIsMobile();

  // Data state
  const [inspections, setInspections] = useState([]);
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters & view
  const [viewMode, setViewMode] = useState('table');
  const [search, setSearch] = useState('');
  const [type, setType] = useState('');
  const [status, setStatus] = useState('');

  // Add modal
  const [showAddModal, setShowAddModal] = useState(false);
  const [adding, setAdding] = useState(false);
  const [inspectionForm, setInspectionForm] = useState({
    inspector_id: '', inspection_date: '', status: 'Pending', report_url: '', notes: ''
  });

  // View modal
  const [viewingInspection, setViewingInspection] = useState(null);

  // Edit modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingInspection, setEditingInspection] = useState(null);

  /* ── Fetch helpers ─────────────────────────────────────── */
  const fetchProperty = useCallback(async () => {
    if (!propertyId) { setError('No property selected.'); return; }
    try {
      const r = await fetch(getApiEndpoint(`/api/properties/${propertyId}`), { headers: getAuthHeader() });
      if (!r.ok) throw new Error('Failed to fetch property');
      setProperty(await r.json());
    } catch (e) {
      setError('Failed to load property information');
    }
  }, [propertyId]);

  const fetchInspections = useCallback(async () => {
    if (!propertyId) { setError('No property selected.'); setLoading(false); return; }
    try {
      setLoading(true);
      const r = await fetch(getApiEndpoint(`/api/properties/${propertyId}/inspections`), { headers: getAuthHeader() });
      if (!r.ok) throw new Error('Failed to fetch inspections');
      const data = await r.json();
      setInspections(sortNewestFirst(data.inspections || []));
      setError(null);
    } catch (e) {
      setError('Failed to load inspections');
      toast.error('Failed to load inspections');
    } finally {
      setLoading(false);
    }
  }, [propertyId]);

  useEffect(() => {
    if (propertyId) { fetchProperty(); fetchInspections(); }
  }, [propertyId, fetchProperty, fetchInspections]);

  /* ── Add form handlers ──────────────────────────────────── */
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setInspectionForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmitInspection = async (e) => {
    e.preventDefault();
    if (!inspectionForm.inspector_id) { toast.error('Inspector is required'); return; }
    if (!inspectionForm.inspection_date) { toast.error('Inspection date is required'); return; }

    try {
      setAdding(true);
      const payload = {
        property_id: propertyId,
        inspector_id: inspectionForm.inspector_id,
        inspection_date: inspectionForm.inspection_date,
        status: inspectionForm.status || 'Pending',
        report_url: inspectionForm.report_url || null,
        notes: inspectionForm.notes || null
      };

      const r = await fetch(getApiEndpoint(`/api/properties/${propertyId}/inspections`), {
        method: 'POST',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      let data = null;
      try { data = await r.json(); } catch (_) {}

      if (!r.ok) {
        const msg = (data && (data.error || data.message)) || `${r.status} ${r.statusText}`;
        throw new Error(msg);
      }

      const createdInspection = data?.inspection || data;
      setInspections(prev => insertNewestFirst(prev, createdInspection));
      setShowAddModal(false);
      setInspectionForm({ inspector_id: '', inspection_date: '', status: 'Pending', report_url: '', notes: '' });
      toast.success('Inspection created successfully');
    } catch (e) {
      toast.error(`Failed to create inspection: ${e.message}`);
    } finally {
      setAdding(false);
    }
  };

  const handleCloseAddModal = () => {
    setShowAddModal(false);
    setInspectionForm({ inspector_id: '', inspection_date: '', status: 'Pending', report_url: '', notes: '' });
  };

  /* ── Action handlers ────────────────────────────────────── */
  const handleView = (insp) => {
    setViewingInspection(insp);
  };

  const handleEdit = (insp) => {
    setEditingInspection({
      ...insp,
      inspection_date: fmtDateInput(insp.scheduledDate),
      inspector_id: insp.inspector || '',
      report_url: insp.report_url || '',
      notes: insp.notes || '',
      status: insp.status || 'Pending'
    });
    setShowEditModal(true);
  };

  const handleUpdateInspection = async (e) => {
    e.preventDefault();
    if (!editingInspection) return;

    try {
      const payload = {
        inspector_id: editingInspection.inspector_id || null,
        inspection_date: editingInspection.inspection_date || null,
        status: editingInspection.status,
        report_url: editingInspection.report_url || null,
        notes: editingInspection.notes || null
      };

      const r = await fetch(getApiEndpoint(`/api/properties/${propertyId}/inspections/${editingInspection.id}`), {
        method: 'PUT',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      let data = null;
      try { data = await r.json(); } catch (_) {}
      if (!r.ok) throw new Error((data && data.error) || 'Failed to update');

      const updated = data?.inspection || data;
      setInspections(prev => prev.map(i => i.id === editingInspection.id ? updated : i));
      setShowEditModal(false);
      setEditingInspection(null);
      toast.success('Inspection updated');
    } catch (e) {
      toast.error(`Failed to update: ${e.message}`);
    }
  };

  const handleMarkComplete = async (insp) => {
    if (insp.status === 'completed' || insp.status === 'Completed') {
      toast.info('Already completed');
      return;
    }
    try {
      const r = await fetch(getApiEndpoint(`/api/properties/${propertyId}/inspections/${insp.id}`), {
        method: 'PUT',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'completed' })
      });

      let data = null;
      try { data = await r.json(); } catch (_) {}
      if (!r.ok) throw new Error((data && data.error) || 'Failed to update');

      const updated = data?.inspection || data;
      setInspections(prev => prev.map(i => i.id === insp.id ? updated : i));
      toast.success('Marked as completed');
    } catch (e) {
      toast.error(`Failed to complete: ${e.message}`);
    }
  };

  const handleDelete = async (insp) => {
    if (!window.confirm(`Delete "${insp.name}"? This cannot be undone.`)) return;
    try {
      const r = await fetch(getApiEndpoint(`/api/properties/${propertyId}/inspections/${insp.id}`), {
        method: 'DELETE',
        headers: getAuthHeader()
      });
      if (!r.ok) {
        let data = null;
        try { data = await r.json(); } catch (_) {}
        throw new Error((data && data.error) || 'Failed to delete');
      }
      setInspections(prev => prev.filter(i => i.id !== insp.id));
      toast.success('Inspection deleted');
    } catch (e) {
      toast.error(`Failed to delete: ${e.message}`);
    }
  };

  /* ── Derived data ───────────────────────────────────────── */
  const filteredInspections = inspections.filter(i =>
    (!type || i.type === type) &&
    (!status || i.status === status) &&
    (!search || (i.name || '').toLowerCase().includes(search.toLowerCase()))
  );

  const now = new Date();
  const overdueCount = inspections.filter(i => {
    const s = (i.status || '').toLowerCase();
    if (s === 'overdue' || s === 'missed') return true;
    if (!i.scheduledDate) return false;
    const d = new Date(i.scheduledDate);
    return !isNaN(d.getTime()) && d < now && s !== 'completed';
  }).length;

  const dueSoonCount = inspections.filter(i => {
    if (!i.scheduledDate) return false;
    const s = (i.status || '').toLowerCase();
    if (s === 'completed') return false;
    const d = new Date(i.scheduledDate);
    const limit = new Date(); limit.setDate(limit.getDate() + 7);
    return !isNaN(d.getTime()) && d >= now && d <= limit;
  }).length;

  const completedCount = inspections.filter(i => {
    const s = (i.status || '').toLowerCase();
    return s === 'completed';
  }).length;

  const propertyName = property?.name || (propertyId ? 'Loading...' : 'All Properties');
  const propertyAddress = property?.address || '';

  /* ── Action buttons fragment ────────────────────────────── */
  const ActionButtons = ({ inspection, mobile }) => {
    const cls = mobile ? 'insp-mobile-action-btn' : 'insp-action-btn';
    return (
      <>
        <button className={cls} title="View" aria-label={`View ${inspection.name}`} onClick={() => handleView(inspection)}>
          <FontAwesomeIcon icon={faEye} />
        </button>
        <button className={cls} title="Complete" aria-label={`Mark ${inspection.name} complete`} onClick={() => handleMarkComplete(inspection)}>
          <FontAwesomeIcon icon={faCheckCircle} />
        </button>
        <button className={cls} title="Edit" aria-label={`Edit ${inspection.name}`} onClick={() => handleEdit(inspection)}>
          <FontAwesomeIcon icon={faPencilAlt} />
        </button>
        <button className={`${cls} insp-action-delete`} title="Delete" aria-label={`Delete ${inspection.name}`} onClick={() => handleDelete(inspection)}>
          <FontAwesomeIcon icon={faTrash} />
        </button>
      </>
    );
  };

  /* ── Loading state ──────────────────────────────────────── */
  if (loading) {
    return (
      <div className="insp-page">

        <div className="insp-content">
          <div className="insp-loading-state">
            <FontAwesomeIcon icon={faSpinner} spin className="insp-loading-icon" />
            <div>Loading inspections...</div>
          </div>
        </div>
      </div>
    );
  }

  /* ── Error state (no property loaded) ───────────────────── */
  if (error && !property) {
    return (
      <div className="insp-page">

        <div className="insp-content">
          <div className="insp-error-state">
            <FontAwesomeIcon icon={faExclamationTriangle} className="insp-error-icon" />
            <div>{error}</div>
            <button className="insp-retry-btn" onClick={() => { fetchProperty(); fetchInspections(); }}>
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  /* ── Main render ────────────────────────────────────────── */
  return (
    <div className="insp-page">
      <div className="insp-content">
        {/* ─ Header ─ */}
        <div className="insp-page-header">
          <div className="insp-header-left">
            <h1 className="insp-page-title">
              <FontAwesomeIcon icon={faClipboardList} className="insp-page-title-icon" />
              Inspections
            </h1>
            {propertyName !== 'Loading...' && <div className="insp-property-name">{propertyName}</div>}
            {propertyAddress && <div className="insp-property-address">{propertyAddress}</div>}
          </div>
          <button className="insp-new-btn" onClick={() => setShowAddModal(true)}>
            <FontAwesomeIcon icon={faPlus} /> New Inspection
          </button>
        </div>

        {/* ─ Stats row ─ */}
        {inspections.length > 0 && (
          <div className="insp-stats-row">
            <div className="insp-stat-card">
              <div className="insp-stat-value">{inspections.length}</div>
              <div className="insp-stat-label">Total Inspections</div>
            </div>
            <div className="insp-stat-card insp-stat-green">
              <div className="insp-stat-value">{completedCount}</div>
              <div className="insp-stat-label">Completed</div>
            </div>
            <div className={`insp-stat-card ${overdueCount > 0 ? 'insp-stat-alert' : ''}`}>
              <div className="insp-stat-value">{overdueCount}</div>
              <div className="insp-stat-label">Overdue</div>
            </div>
            <div className={`insp-stat-card ${dueSoonCount > 0 ? 'insp-stat-warn' : ''}`}>
              <div className="insp-stat-value">{dueSoonCount}</div>
              <div className="insp-stat-label">Due Soon</div>
            </div>
          </div>
        )}

        {/* ─ Filters ─ */}
        <div className="insp-filters">
          <div className="insp-search-wrap">
            <FontAwesomeIcon icon={faSearch} className="insp-search-icon" />
            <input type="text" className="insp-search-input" placeholder="Search inspections..." value={search} onChange={e => setSearch(e.target.value)} aria-label="Search inspections by name" />
            {search && (
              <button className="insp-search-clear" onClick={() => setSearch('')} aria-label="Clear search">
                <FontAwesomeIcon icon={faTimes} />
              </button>
            )}
          </div>

          <select className="insp-filter-select" value={type} onChange={e => setType(e.target.value)} aria-label="Filter by inspection type">
            <option value="">All Types</option>
            <option value="Safety">Safety</option>
            <option value="Equipment">Equipment</option>
            <option value="Emergency">Emergency</option>
            <option value="Fire Safety">Fire Safety</option>
            <option value="Structural">Structural</option>
            <option value="General">General</option>
          </select>

          <select className="insp-filter-select" value={status} onChange={e => setStatus(e.target.value)} aria-label="Filter by status">
            <option value="">All Statuses</option>
            <option value="scheduled">Scheduled</option>
            <option value="in_progress">In Progress</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
            <option value="Pending">Pending</option>
          </select>

          <button className="insp-refresh-btn" onClick={fetchInspections} title="Refresh">
            <FontAwesomeIcon icon={faSyncAlt} />
          </button>
        </div>

        {/* ─ Inspection list ─ */}
        {filteredInspections.length === 0 ? (
          <div className="insp-empty-wrap">
            <div className="insp-empty-card">
              <FontAwesomeIcon icon={faClipboardList} className="insp-empty-icon" />
              <div className="insp-empty-title">
                {inspections.length === 0 ? 'No inspections yet' : 'No inspections match your filters'}
              </div>
              <div className="insp-empty-desc">
                {inspections.length === 0
                  ? 'Create your first inspection to get started.'
                  : 'Try adjusting your filters or search term.'}
              </div>
              {inspections.length === 0 && (
                <button className="insp-new-btn" onClick={() => setShowAddModal(true)}>
                  <FontAwesomeIcon icon={faPlus} /> New Inspection
                </button>
              )}
            </div>
          </div>
        ) : isMobile ? (
          <div className="insp-mobile-list">
            {filteredInspections.map(insp => (
              <div key={insp.id} className="insp-mobile-card">
                <div className="insp-mobile-card-top">
                  <div className="insp-mobile-file-icon">
                    <FontAwesomeIcon icon={faClipboardList} />
                  </div>
                  <div className="insp-mobile-card-info">
                    <h3 className="insp-mobile-card-name">{insp.name}</h3>
                    <div className="insp-mobile-badges">
                      {insp.type && <span className="insp-type-badge">{insp.type}</span>}
                      <StatusBadge status={normStatus(insp.status)} dot size="sm" />
                    </div>
                  </div>
                </div>
                <div className="insp-mobile-meta">
                  <div className="insp-mobile-meta-left">
                    {insp.inspector && <span>Inspector: {insp.inspector}</span>}
                    <span>
                      {insp.scheduledDate
                        ? <>Scheduled: {fmtDate(insp.scheduledDate)}</>
                        : `Created: ${fmtDate(insp.created_at)}`}
                    </span>
                  </div>
                  <div className="insp-mobile-actions">
                    <ActionButtons inspection={insp} mobile />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : viewMode === 'table' ? (
          <div className="insp-table-card">
            <div className="insp-table-scroll">
              <table className="insp-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Type</th>
                    <th>Scheduled Date</th>
                    <th>Status</th>
                    <th>Inspector</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredInspections.map(insp => (
                    <tr key={insp.id}>
                      <td>
                        <div className="insp-name-cell">
                          <FontAwesomeIcon icon={faClipboardList} className="insp-name-icon" />
                          <span className="insp-name-text" title={insp.name}>{insp.name}</span>
                        </div>
                      </td>
                      <td><span className="insp-type-badge">{insp.type}</span></td>
                      <td className="insp-date-cell">{fmtDate(insp.scheduledDate)}</td>
                      <td><StatusBadge status={normStatus(insp.status)} dot size="sm" /></td>
                      <td className="insp-date-cell">{insp.inspector || '—'}</td>
                      <td>
                        <div className="insp-actions-cell">
                          <ActionButtons inspection={insp} />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="insp-grid">
            {filteredInspections.map(insp => (
              <div key={insp.id} className="insp-grid-card">
                <div className="insp-grid-card-header">
                  <div className="insp-grid-file-icon">
                    <FontAwesomeIcon icon={faClipboardList} />
                  </div>
                  <div className="insp-grid-card-info">
                    <h3 className="insp-grid-card-name">{insp.name}</h3>
                    <div className="insp-grid-card-type">{insp.type}</div>
                  </div>
                </div>
                <div className="insp-grid-card-meta">
                  <div className="insp-grid-card-details">
                    <StatusBadge status={normStatus(insp.status)} dot size="sm" />
                    {insp.inspector && <span>Inspector: {insp.inspector}</span>}
                    <span>Scheduled: {fmtDate(insp.scheduledDate)}</span>
                  </div>
                  <div className="insp-grid-card-actions">
                    <ActionButtons inspection={insp} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ─ Footer ─ */}
        <div className="insp-footer">
          <span>{filteredInspections.length} inspection{filteredInspections.length !== 1 ? 's' : ''}</span>
        </div>
      </div>

      {/* ── Add Inspection Modal ── */}
      {showAddModal && (
        <div className="insp-modal-overlay" onClick={handleCloseAddModal}>
          <div className="insp-modal" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Add Inspection">
            <div className="insp-modal-header">
              <h3 className="insp-modal-title">
                <FontAwesomeIcon icon={faClipboardList} className="insp-modal-title-icon" />
                Add Inspection
              </h3>
              <button className="insp-modal-close" onClick={handleCloseAddModal} disabled={adding} aria-label="Close">
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <form onSubmit={handleSubmitInspection} className="insp-form">
              <div className="insp-form-row">
                <div className="insp-form-group">
                  <label className="insp-form-label" htmlFor="insp-inspector">Inspector <span className="insp-required">*</span></label>
                  <select id="insp-inspector" name="inspector_id" className="insp-form-select" value={inspectionForm.inspector_id} onChange={handleFormChange} required disabled={adding}>
                    <option value="">Select Inspector</option>
                    <option value="1">Joshua Tobias-Gibbins</option>
                    <option value="2">John Inspector</option>
                    <option value="3">Sam Hoad</option>
                    <option value="5">Josh Con Tobias-Gibbins</option>
                  </select>
                </div>
                <div className="insp-form-group">
                  <label className="insp-form-label" htmlFor="insp-date">Inspection Date <span className="insp-required">*</span></label>
                  <input type="date" id="insp-date" name="inspection_date" className="insp-form-input" value={inspectionForm.inspection_date} onChange={handleFormChange} required disabled={adding} />
                </div>
              </div>
              <div className="insp-form-row">
                <div className="insp-form-group">
                  <label className="insp-form-label" htmlFor="insp-status-add">Status <span className="insp-required">*</span></label>
                  <select id="insp-status-add" name="status" className="insp-form-select" value={inspectionForm.status} onChange={handleFormChange} disabled={adding}>
                    <option value="scheduled">Scheduled</option>
                    <option value="in_progress">In Progress</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
                <div className="insp-form-group">
                  <label className="insp-form-label" htmlFor="insp-report-url">Report URL</label>
                  <input type="url" id="insp-report-url" name="report_url" className="insp-form-input" value={inspectionForm.report_url} onChange={handleFormChange} placeholder="https://example.com/report.pdf" disabled={adding} />
                </div>
              </div>
              <div className="insp-form-group">
                <label className="insp-form-label" htmlFor="insp-notes">Notes</label>
                <textarea id="insp-notes" name="notes" className="insp-form-textarea" value={inspectionForm.notes} onChange={handleFormChange} placeholder="Inspection notes and findings" rows="3" disabled={adding} />
              </div>
            </form>
            <div className="insp-modal-actions">
              <button type="button" className="insp-btn-cancel" onClick={handleCloseAddModal} disabled={adding}>Cancel</button>
              <button type="submit" className="insp-btn-submit" disabled={adding} onClick={handleSubmitInspection}>
                {adding ? <><FontAwesomeIcon icon={faSpinner} spin /> Adding...</> : <><FontAwesomeIcon icon={faPlus} /> Add Inspection</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── View Inspection Modal ── */}
      {viewingInspection && (
        <div className="insp-modal-overlay" onClick={() => setViewingInspection(null)}>
          <div className="insp-modal" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Inspection Details">
            <div className="insp-modal-header">
              <h3 className="insp-modal-title">
                <FontAwesomeIcon icon={faEye} className="insp-modal-title-icon" />
                {viewingInspection.name}
              </h3>
              <button className="insp-modal-close" onClick={() => setViewingInspection(null)} aria-label="Close">
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <div className="insp-form" style={{ padding: '20px 24px' }}>
              <div className="insp-form-row">
                <div className="insp-form-group">
                  <div className="insp-form-label">Status</div>
                  <div><StatusBadge status={normStatus(viewingInspection.status)} dot size="md" /></div>
                </div>
                <div className="insp-form-group">
                  <div className="insp-form-label">Type</div>
                  <div><span className="insp-type-badge">{viewingInspection.type || '—'}</span></div>
                </div>
              </div>
              <div className="insp-form-row">
                <div className="insp-form-group">
                  <div className="insp-form-label">Scheduled Date</div>
                  <div style={{ fontSize: '0.875rem', color: '#374151' }}>{fmtDate(viewingInspection.scheduledDate)}</div>
                </div>
                <div className="insp-form-group">
                  <div className="insp-form-label">Inspector</div>
                  <div style={{ fontSize: '0.875rem', color: '#374151' }}>{viewingInspection.inspector || '—'}</div>
                </div>
              </div>
              {viewingInspection.report_url && (
                <div className="insp-form-group">
                  <div className="insp-form-label">Report</div>
                  <a href={viewingInspection.report_url} target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.875rem', color: '#1A80E5', wordBreak: 'break-all' }}>
                    {viewingInspection.report_url}
                  </a>
                </div>
              )}
              {viewingInspection.notes && (
                <div className="insp-form-group">
                  <div className="insp-form-label">Notes</div>
                  <div style={{ fontSize: '0.875rem', color: '#374151', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{viewingInspection.notes}</div>
                </div>
              )}
            </div>
            <div className="insp-modal-actions">
              <button type="button" className="insp-btn-cancel" onClick={() => setViewingInspection(null)}>Close</button>
              <button type="button" className="insp-btn-submit" onClick={() => { setViewingInspection(null); handleEdit(viewingInspection); }}>
                <FontAwesomeIcon icon={faPencilAlt} /> Edit
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Edit Inspection Modal ── */}
      {showEditModal && editingInspection && (
        <div className="insp-modal-overlay" onClick={() => { setShowEditModal(false); setEditingInspection(null); }}>
          <div className="insp-modal" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Edit Inspection">
            <div className="insp-modal-header">
              <h3 className="insp-modal-title">
                <FontAwesomeIcon icon={faPencilAlt} className="insp-modal-title-icon" />
                Edit Inspection
              </h3>
              <button className="insp-modal-close" onClick={() => { setShowEditModal(false); setEditingInspection(null); }} aria-label="Close">
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <form onSubmit={handleUpdateInspection} className="insp-form">
              <div className="insp-form-row">
                <div className="insp-form-group">
                  <label className="insp-form-label" htmlFor="insp-edit-inspector">Inspector</label>
                  <select id="insp-edit-inspector" className="insp-form-select" value={editingInspection.inspector_id} onChange={e => setEditingInspection(prev => ({ ...prev, inspector_id: e.target.value }))}>
                    <option value="">Select Inspector</option>
                    <option value="1">Joshua Tobias-Gibbins</option>
                    <option value="2">John Inspector</option>
                    <option value="3">Sam Hoad</option>
                    <option value="5">Josh Con Tobias-Gibbins</option>
                  </select>
                </div>
                <div className="insp-form-group">
                  <label className="insp-form-label" htmlFor="insp-edit-date">Inspection Date</label>
                  <input type="date" id="insp-edit-date" className="insp-form-input" value={editingInspection.inspection_date} onChange={e => setEditingInspection(prev => ({ ...prev, inspection_date: e.target.value }))} />
                </div>
              </div>
              <div className="insp-form-row">
                <div className="insp-form-group">
                  <label className="insp-form-label" htmlFor="insp-edit-status">Status</label>
                  <select id="insp-edit-status" className="insp-form-select" value={editingInspection.status} onChange={e => setEditingInspection(prev => ({ ...prev, status: e.target.value }))}>
                    <option value="scheduled">Scheduled</option>
                    <option value="in_progress">In Progress</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
                <div className="insp-form-group">
                  <label className="insp-form-label" htmlFor="insp-edit-report">Report URL</label>
                  <input type="url" id="insp-edit-report" className="insp-form-input" value={editingInspection.report_url} onChange={e => setEditingInspection(prev => ({ ...prev, report_url: e.target.value }))} placeholder="https://example.com/report.pdf" />
                </div>
              </div>
              <div className="insp-form-group">
                <label className="insp-form-label" htmlFor="insp-edit-notes">Notes</label>
                <textarea id="insp-edit-notes" className="insp-form-textarea" value={editingInspection.notes} onChange={e => setEditingInspection(prev => ({ ...prev, notes: e.target.value }))} placeholder="Inspection notes and findings" rows="3" />
              </div>
            </form>
            <div className="insp-modal-actions">
              <button type="button" className="insp-btn-cancel" onClick={() => { setShowEditModal(false); setEditingInspection(null); }}>Cancel</button>
              <button type="submit" className="insp-btn-submit" onClick={handleUpdateInspection}>
                <FontAwesomeIcon icon={faPencilAlt} /> Update
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
